import React, { useContext, useEffect } from 'react';
import {Box, IconButton, Link, MenuItem, Typography} from '@material-ui/core';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {useTranslation} from 'react-i18next';
import useTileStyles from '../common/tileCommonStyles';
import FormikTextField from '../../common/form/FormikTextField/FormikTextField';
import i18nNamespaces from '../../../const/i18nNamespaces';
import { ewusApi } from '../../../api/ewus/ewusApi';
import { RemoveCircleOutline } from '@material-ui/icons';
import Loading from '../../common/Loading/Loading';
import { useStyles } from './EwusSecretFormStyles';
import CustomButton from '../../common/Button/Button';
import { insuranceDeclarationApi } from '../../../api/insuranceDeclaration/insuranceDeclarationApi';
import { RegionalBranch } from '../../../types/insuranceDeclaration';
import { AlertContext } from '../../../contexts/AlertContext';
import axios from 'axios';
import { ewusMedicCredentialsErrorHandler } from '../../../utils/error';

type EwusSecretFormValues = {
    login: string;
    password: string;
    domain: string;
    type: string;
    idntLek: string;
    secret: string;
};

const DOMAIN_CODES_WITHOUT_IDNT_LEK = ['02', '03', '07', '10', '13', '14', '15', '16'];

const EwusSecretForm = () => {
    const classes = useStyles();
    const tileClasses = useTileStyles();
    const openAlert = useContext(AlertContext);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [areCredentialsFilled, setAreCredentialsFilled] = React.useState<boolean>(false);
    const [isCredentialsResolved, setIsCredentialsResolved] = React.useState<boolean>(false);
    const [regionalBranchList, setRegionalBranchList] = React.useState<RegionalBranch[]>([]);
    const {t: commonT} = useTranslation(i18nNamespaces.COMMON);
    const {t: prescriptionsT} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const {t: medicT} = useTranslation(i18nNamespaces.MEDIC);
    const {t: errorsT} = useTranslation(i18nNamespaces.ERRORS);

    const isIdntLekRequired = (domain: string) => !DOMAIN_CODES_WITHOUT_IDNT_LEK.includes(domain);

    const formik = useFormik<EwusSecretFormValues>({
        initialValues: {
            login: '',
            password: '',
            domain: '',
            type: 'LEK',
            idntLek: '',
            secret: '',
        },
        validationSchema: yup.object({
            login: yup.string().trim().required(commonT('requiredField')),
            password: yup.string().trim().required(commonT('requiredField')),
            domain: yup.string().trim().required(commonT('requiredField')),
            idntLek: yup.string().trim().when('domain', {
                is: (domain: string) => isIdntLekRequired(domain),
                then: schema => schema.required(commonT('requiredField')),
                otherwise: schema => schema,
            }),
            secret: yup.string().trim().required(commonT('requiredField')),
        }),
        onSubmit: async (values, {resetForm}) => {
            try {
                setLoading(true);
                const {data} = await ewusApi.setEwusMedicCredentials(values);
                setAreCredentialsFilled(data.isSuccess);
                if(data.isSuccess) 
                    openAlert?.({message: medicT('ewusCredentialsSaved'), severity: 'success'});
                else
                    openAlert?.({message: medicT('ewusCredentialsNotSaved'), severity: 'error'});
            } catch (error) {
                console.error(error);
                if(axios.isAxiosError(error)) {
                    openAlert?.({message: errorsT(ewusMedicCredentialsErrorHandler(error.response?.data[0])), severity: 'error'});
                } else
                    openAlert?.({message: medicT('ewusCredentialsNotSaved'), severity: 'error'});
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        fetchEwusCredentials();
    }, []);

    useEffect(() => {
        if (!isCredentialsResolved || areCredentialsFilled) {
            return;
        }

        fetchRegionalBranches();
    }, [areCredentialsFilled, isCredentialsResolved]);

    useEffect(() => {
        if (!isIdntLekRequired(formik.values.domain) && formik.values.idntLek) {
            formik.setFieldValue('idntLek', '');
        }
    }, [formik.values.domain, formik.values.idntLek]);

    const fetchRegionalBranches = async () => {
        try {
            const {data} = await insuranceDeclarationApi.getNfzRegionalBranch();
            setRegionalBranchList(data.result);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchEwusCredentials = async () => {
        try {
            setLoading(true);
            const {data} = await ewusApi.getEwusMedicCredentials();
            setAreCredentialsFilled(data.isSuccess);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setIsCredentialsResolved(true);
        }
    };

    const deleteEwusCredentials = async () => {
        try {
            setLoading(true);
            const {data} = await ewusApi.deleteEwusMedicCredentials();
            setAreCredentialsFilled(!data.isSuccess);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if(areCredentialsFilled) {
        return (
            <div className={tileClasses.root}>
                <Loading loading={loading} withBackdrop/>
                <h3 className={tileClasses.headerText}>{prescriptionsT('ewus')}</h3>
                <Box className={classes.loadedCredentialsWrapper}>
                    <Typography component={'span'}>{prescriptionsT('loaded')}</Typography>
                    <IconButton onClick={deleteEwusCredentials} size='small' color='secondary'>
                        <RemoveCircleOutline fontSize='small'/>
                    </IconButton>
                </Box>
            </div>
        );
    }

    return (
        <div className={tileClasses.root}>
            <Loading loading={loading} withBackdrop/>
            <h3 className={tileClasses.headerText}>{prescriptionsT('ewus')}</h3>
            <form onSubmit={formik.handleSubmit}>
                <Box className={classes.formWrapper}>
                    <Box className={classes.formColumn}>
                        <FormikTextField
                            form={formik}
                            label={`${medicT('login')} *`}
                            fieldName={'login'}
                            fullWidth
                            noStyles
                        />
                        <FormikTextField
                            form={formik}
                            label={`${medicT('password')} *`}
                            fieldName={'password'}
                            type={'password'}
                            autoComplete={'new-password'}
                            fullWidth
                            noStyles
                        />
                    </Box>
                    <Box className={classes.formColumn}>
                        <FormikTextField
                            form={formik}
                            label={`${medicT('domain')} *`}
                            fieldName={'domain'}
                            select
                            fullWidth
                            noStyles
                        >
                            {regionalBranchList.map(branch => (
                                <MenuItem key={branch.code} value={branch.code}>
                                    {branch.code} - {branch.name}
                                </MenuItem>
                            ))}
                        </FormikTextField>
                        {isIdntLekRequired(formik.values.domain) && (
                            <FormikTextField
                                form={formik}
                                label={`${medicT('idntLek')} *`}
                                fieldName={'idntLek'}
                                fullWidth
                                noStyles
                            />
                        )}
                        <FormikTextField
                            form={formik}
                            label={`${medicT('secret')} *`}
                            fieldName={'secret'}
                            type={'password'}
                            autoComplete={'new-password'}
                            fullWidth
                            noStyles
                        />
                    </Box>
                </Box>
                <CustomButton
                    className={classes.saveButton}
                    variant={'contained'}
                    color={'secondary'}
                    type={'submit'}
                >
                    {commonT('save')}
                </CustomButton>
            </form>
        </div>
    );
};

export default EwusSecretForm;